import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo, useEffect } from 'react';
import { useConfirm } from "@omit/react-confirm-dialog";
import { Button } from "@/modern/components/ui/button";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { getTaskStatusHistory } from "@/network/network";
import { ALERT_TYPES, ReminderTypes } from "@/other/constants/OtherKeys";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Reminder } from '@/bryntum-lib/models/Reminder';
import {
  TimelineData,
  ModalState,
  EntityConfig,
  EntityRecord,
  StatusChange,
  ResourceDetail,
  PlanningEvent,
  HistoryEvent,
  TimelineMode,
  FilterKey,
  getEntityConfig,
  getEntityIdField
} from '../utils/types';
import {
  transformStatusHistory,
  transformScheduleDates,
  transformPlanningDates,
  transformReminderDates,
  transformHistoryDates,
  transformResourceDetails,
  getResourceIdFromEvent
} from '../utils/transformers';
import { EntityTypeKey, EntityWidgetProps } from '@/other/constants/EntityManagerConfig';
import { WidgetParamKeys } from "@/types/entity";
import { PERSISTENCE_STATE_KEYS } from '@/other/constants/PersistenceStateKeys';
import { PlanningFormData } from '../editors/PlanningEditModal';
import { ISwivuProject } from "@/types/SwivuProject";

interface TimelineContextValue {
  // Entity info
  record: EntityRecord;
  entityType: EntityTypeKey;
  project: ISwivuProject | null;

  // Timeline data
  timelineData: TimelineData;
  isLoading: boolean;
  isInitialLoad: boolean;

  // Selected mode (single selection instead of multiple filters)
  selectedMode: TimelineMode;
  setSelectedMode: (mode: TimelineMode) => void;

  // Modal states
  reminderModal: ModalState<Reminder>;
  statusModal: ModalState<StatusChange[]>;
  planningModal: ModalState<PlanningEvent[]>;
  resourceModal: ModalState<ResourceDetail[]>;
  historyModal: ModalState<HistoryEvent>;

  // Modal handlers
  openReminderModal: (reminder: Reminder) => void;
  closeReminderModal: () => void;
  openStatusModal: (statusChanges: StatusChange[]) => void;
  closeStatusModal: () => void;
  openPlanningModal: (planningEvents: PlanningEvent[]) => void;
  closePlanningModal: () => void;
  openResourceModal: (resources: ResourceDetail[]) => void;
  closeResourceModal: () => void;
  openHistoryModal: (historyEvent: HistoryEvent) => void;
  closeHistoryModal: () => void;

  // Edit modal states
  reminderEditModal: { isOpen: boolean; mode: "create" | "edit"; reminderId?: string; defaultFormValues?: Partial<{ alertDate: Date; description: string }> };
  setReminderEditModal: React.Dispatch<React.SetStateAction<{ isOpen: boolean; mode: "create" | "edit"; reminderId?: string; defaultFormValues?: Partial<{ alertDate: Date; description: string }> }>>;
  scheduleEditModal: { isOpen: boolean; mode: "create" | "edit"; eventId?: string };
  setScheduleEditModal: React.Dispatch<React.SetStateAction<{ isOpen: boolean; mode: "create" | "edit"; eventId?: string }>>;
  planningEditModal: { isOpen: boolean; mode: "create" | "edit"; planningId?: string; defaultFormValues?: Partial<PlanningFormData> };
  setPlanningEditModal: React.Dispatch<React.SetStateAction<{ isOpen: boolean; mode: "create" | "edit"; planningId?: string; defaultFormValues?: Partial<PlanningFormData> }>>;

  // CRUD operations
  handleCreateReminder: (defaultFormValues?: Partial<{ alertDate: Date; description: string }>) => void;
  handleEditReminder: (reminderId: string) => void;
  handleDeleteReminder: (reminderId: string) => Promise<void>;
  handleCreateSchedule: () => void;
  handleEditSchedule: (eventId: string) => void;
  handleDeleteSchedule: (eventId: string) => Promise<void>;
  handleCreatePlanning: (defaultFormValues?: Partial<PlanningFormData>) => void;
  handleEditPlanning: (planningId: string) => void;
  handleDeletePlanning: (planningId: string) => Promise<void>;

  // Click handlers
  handleStatusClick: (statusChange: StatusChange) => void;
  handlePlanningEventClick: (planningEvent: PlanningEvent) => void;
  handleScheduleResourceClick: (scheduleEvents: Record<string, unknown>[]) => void;
  handleReminderClick: (reminder: Reminder) => void;
  handleHistoryClick: (historyEvent: HistoryEvent) => void;

  // Config based on entity type
  config: EntityConfig;

  onClose?: () => void;
  headerTitle?: string;
}

const TimelineContext = createContext<TimelineContextValue | undefined>(undefined);

interface TimelineProviderProps extends EntityWidgetProps {
  children: ReactNode;
  initialMode?: TimelineMode;
}

export const TimelineProvider: React.FC<TimelineProviderProps> = ({ children, record, entityType, onClose, headerTitle, initialMode }) => {
  const { project, entityManager } = useSwivuBryntumContext();
  const { callParamAction } = entityManager;
  const confirm = useConfirm();

  // Get entity-specific configuration
  const config = useMemo(() => getEntityConfig(entityType), [entityType]);

  // Initialize selected mode - default to "status" for tasks, "history" for others, or first available
  const getDefaultMode = (): TimelineMode => {
    if (config.showStatusHistory) return "status";
    if (config.showHistory) return "history";
    return config.availableFilters[0] || null;
  };

  const resolveInitialMode = (): TimelineMode => {
    if (initialMode && config.availableFilters.includes(initialMode)) {
      return initialMode;
    }
    return getDefaultMode();
  };

  const [selectedMode, setSelectedModeState] = useState<TimelineMode>(resolveInitialMode);

  // Load persisted mode preference (skipped when opened with a column-specific initialMode)
  useEffect(() => {
    if (initialMode && config.availableFilters.includes(initialMode)) {
      setSelectedModeState(initialMode);
      return;
    }
    if (project?.userPreferencesStore) {
      const { userPreferencesStore } = project;
      const timelineMode = userPreferencesStore.getSetting(PERSISTENCE_STATE_KEYS.timelineFiltersState);
      if (timelineMode && config.availableFilters.includes(timelineMode as FilterKey)) {
        setSelectedModeState(timelineMode as TimelineMode);
      }
    }
  }, [initialMode, project?.userPreferencesStore, config.availableFilters]);

  // Update selected mode and persist
  const setSelectedMode = useCallback((mode: TimelineMode) => {
    setSelectedModeState(mode);
    if (project?.userPreferencesStore && mode) {
      project.userPreferencesStore.updateSetting(PERSISTENCE_STATE_KEYS.timelineFiltersState, mode);
    }
  }, [project]);

  const [timelineData, setTimelineData] = useState<TimelineData>({
    entityId: (record?.id as string) || "",
    entityName: (record?.name as string) || "",
    statusChanges: [],
    scheduleDates: [],
    planningDates: [],
    historyDates: [],
    reminderDates: []
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Modal states
  const [reminderModal, setReminderModal] = useState<ModalState<Reminder>>({ isOpen: false, data: null });
  const [statusModal, setStatusModal] = useState<ModalState<StatusChange[]>>({ isOpen: false, data: null });
  const [planningModal, setPlanningModal] = useState<ModalState<PlanningEvent[]>>({ isOpen: false, data: null });
  const [resourceModal, setResourceModal] = useState<ModalState<ResourceDetail[]>>({ isOpen: false, data: null });
  const [historyModal, setHistoryModal] = useState<ModalState<HistoryEvent>>({ isOpen: false, data: null });

  // Edit modal states
  const [reminderEditModal, setReminderEditModal] = useState<{ isOpen: boolean; mode: "create" | "edit"; reminderId?: string; defaultFormValues?: Partial<{ alertDate: Date; description: string }> }>({
    isOpen: false,
    mode: "create"
  });
  const [scheduleEditModal, setScheduleEditModal] = useState<{ isOpen: boolean; mode: "create" | "edit"; eventId?: string }>({
    isOpen: false,
    mode: "create"
  });
  const [planningEditModal, setPlanningEditModal] = useState<{ isOpen: boolean; mode: "create" | "edit"; planningId?: string; defaultFormValues?: Partial<PlanningFormData> }>({
    isOpen: false,
    mode: "create"
  });

  // Store versions for reactivity
  const { version: reminderVersion } = useSwivuStore({ storeId: STORE_KEYS.reminderStore });
  const { version: reminderRecipientVersion } = useSwivuStore({ storeId: STORE_KEYS.reminderRecipientStore });
  const { version: eventVersion } = useSwivuStore({ storeId: STORE_KEYS.eventStore });
  const { version: assignmentVersion } = useSwivuStore({ storeId: STORE_KEYS.assignmentStore });
  const { version: planningVersion } = useSwivuStore({ storeId: STORE_KEYS.taskPlanningDatesStore });
  const { version: taskVersion } = useSwivuStore({ storeId: STORE_KEYS.taskStore });

  // Fetch and transform data when record changes
  useEffect(() => {
    const fetchTimelineData = async () => {
      if (!record || !project) {
        setIsLoading(false);
        setIsInitialLoad(false);
        return;
      }

      try {
        // Only show loading spinner on initial load, not on auto-refreshes
        if (isInitialLoad) {
          setIsLoading(true);
        }

        // Fetch status history only for tasks
        let statusChanges: StatusChange[] = [];
        if (config.showStatusHistory) {
          try {
            const statusResponse = await getTaskStatusHistory(record.id);
            statusChanges = transformStatusHistory(statusResponse.data || []);
          } catch (error) {
            console.warn("Failed to fetch status history:", error);
          }
        }

        // Transform schedule data from events (only for tasks)
        const scheduleDates = config.showSchedule && 'events' in record
          ? transformScheduleDates((record.events as Record<string, unknown>[]) || [])
          : [];

        // Transform planning dates (only for tasks)
        const planningDates = config.showPlanning && 'planningDatesRecords' in record
          ? transformPlanningDates((record.planningDatesRecords as Record<string, unknown>[]) || [])
          : [];

        // Transform history (only for client/resource)
        const historyDates = config.showHistory
          ? transformHistoryDates(record, project)
          : [];

        // Transform reminders (for all entities)
        const entityIdField = getEntityIdField(entityType);
        const entityReminderType = entityType === 'task' ? ReminderTypes.task :
                                    entityType === 'client' ? ReminderTypes.client :
                                    ReminderTypes.resource;

        const entityReminders = project.reminderStore.query(
          (reminder: Reminder) =>
            reminder.entityType === entityReminderType &&
            reminder[entityIdField] === record.id &&
            reminder.alertType === ALERT_TYPES.REMINDER &&
            reminder.alertTag === "Reminder",
          true
        ) || [];
        const reminderDates = transformReminderDates(entityReminders, record);

        setTimelineData({
          entityId: record.id as string,
          entityName: record.name as string,
          statusChanges,
          scheduleDates,
          planningDates,
          historyDates,
          reminderDates
        });
      } catch (error) {
        console.error("Error fetching timeline data:", error);
      } finally {
        if (isInitialLoad) {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
    };

    fetchTimelineData();
  }, [record, project, config, entityType, reminderVersion, reminderRecipientVersion, eventVersion, assignmentVersion, planningVersion, taskVersion, isInitialLoad]);


  // Modal handlers
  const openReminderModal = useCallback((reminder: Reminder) => {
    setReminderModal({ isOpen: true, data: reminder });
  }, []);

  const closeReminderModal = useCallback(() => {
    setReminderModal({ isOpen: false, data: null });
  }, []);

  const openStatusModal = useCallback((statusChanges: StatusChange[]) => {
    setStatusModal({ isOpen: true, data: statusChanges });
  }, []);

  const closeStatusModal = useCallback(() => {
    setStatusModal({ isOpen: false, data: null });
  }, []);

  const openPlanningModal = useCallback((planningEvents: PlanningEvent[]) => {
    setPlanningModal({ isOpen: true, data: planningEvents });
  }, []);

  const closePlanningModal = useCallback(() => {
    setPlanningModal({ isOpen: false, data: null });
  }, []);

  const openResourceModal = useCallback((resources: ResourceDetail[]) => {
    setResourceModal({ isOpen: true, data: resources });
  }, []);

  const closeResourceModal = useCallback(() => {
    setResourceModal({ isOpen: false, data: null });
  }, []);

  const openHistoryModal = useCallback((historyEvent: HistoryEvent) => {
    setHistoryModal({ isOpen: true, data: historyEvent });
  }, []);

  const closeHistoryModal = useCallback(() => {
    setHistoryModal({ isOpen: false, data: null });
  }, []);

  // Click handlers
  const handleStatusClick = useCallback((statusChange: StatusChange) => {
    if (statusChange) {
      openStatusModal([statusChange]);
    } else {
      console.warn("No status change to display");
    }
  }, [openStatusModal]);

  const handlePlanningEventClick = useCallback((planningEvent: PlanningEvent) => {
    if (planningEvent) {
      openPlanningModal([planningEvent]);
    } else {
      console.warn("No planning event to display");
    }
  }, [openPlanningModal]);

  const handleScheduleResourceClick = useCallback((scheduleEvents: Record<string, unknown>[]) => {
    if (!project) {
      console.warn("Project not available for resource details");
      return;
    }

    try {
      // Transform all schedule events to get resource details
      const allResourceDetails: ResourceDetail[] = [];
      const seenResourceIds = new Set<string>();

      scheduleEvents.forEach((event) => {
        const resourceDetails = transformResourceDetails(event, project);

        // Only add unique resources (deduplicate by resource ID)
        resourceDetails.forEach((resourceDetail) => {
          const resourceId = getResourceIdFromEvent(event);
          if (resourceId && !seenResourceIds.has(resourceId)) {
            seenResourceIds.add(resourceId);
            allResourceDetails.push(resourceDetail);
          }
        });
      });

      if (allResourceDetails.length > 0) {
        openResourceModal(allResourceDetails);
      } else {
        console.warn("No resource details found for these schedule events");
      }
    } catch (error) {
      console.error("Error fetching resource details:", error);
    }
  }, [project, openResourceModal]);

  const handleReminderClick = useCallback((reminder: Reminder) => {
    if (reminder) {
      openReminderModal(reminder);
    } else {
      console.warn("No reminder to display");
    }
  }, [openReminderModal]);

  const handleHistoryClick = useCallback((historyEvent: HistoryEvent) => {
    if (historyEvent) {
      openHistoryModal(historyEvent);
    } else {
      console.warn("No history event to display");
    }
  }, [openHistoryModal]);

  // CRUD operations - Reminder
  const handleCreateReminder = useCallback((defaultFormValues?: Partial<{ alertDate: Date; description: string }>) => {
    setReminderEditModal({ isOpen: true, mode: "create", defaultFormValues });
  }, []);

  const handleEditReminder = useCallback((reminderId: string) => {
    setReminderEditModal({ isOpen: true, mode: "edit", reminderId });
  }, []);

  const handleDeleteReminder = useCallback(async (reminderId: string) => {
    if (!project) return;

    const reminderStore = project.reminderStore;
    const reminder = reminderStore?.getById(reminderId);

    if (!reminder) {
      alert("Reminder not found");
      return;
    }

    const confirmDelete = await confirm({
      title: "Confirm Delete",
      description: `Are you sure you want to delete this reminder: "${reminder.description || "Unnamed reminder"}"?`,
      customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-4">
          <Button variant="ghost" onClick={cancel}>
            Cancel
          </Button>
          <Button className="dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors" onClick={confirm}>
            Delete
          </Button>
        </div>
      )
    });

    if (confirmDelete) {
      try {
        reminderStore.remove(reminderId);
      } catch (error) {
        console.error("Error deleting reminder:", error);
        alert("Failed to delete reminder. Please try again.");
      }
    }
  }, [project, confirm]);

  // CRUD operations - Schedule
  const handleCreateSchedule = useCallback(() => {
    setScheduleEditModal({ isOpen: true, mode: "create" });
  }, []);

  const handleEditSchedule = useCallback((eventId: string) => {
    setScheduleEditModal({ isOpen: true, mode: "edit", eventId });
  }, []);

  const handleDeleteSchedule = useCallback(async (eventId: string) => {
    if (!project) return;

    const eventStore = project.eventStore;
    const event = eventStore?.getById(eventId);

    if (!event) {
      alert("Schedule not found");
      return;
    }

    // Get resource name for display
    const assignmentStore = project.assignmentStore;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const assignment = assignmentStore.query((a: any) => a.eventId === eventId, false)[0];
    const resourceStore = project.resourceStore;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resource = assignment ? resourceStore.getById((assignment as any).resourceId as string | number) : null;
    const resourceName = resource?.name || "Unknown resource";

    const confirmDelete = await confirm({
      title: "Confirm Delete",
      description: `Are you sure you want to delete this schedule for "${resourceName}"?`,
      customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-4">
          <Button variant="ghost" onClick={cancel}>
            Cancel
          </Button>
          <Button className="dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors" onClick={confirm}>
            Delete
          </Button>
        </div>
      )
    });

    if (confirmDelete) {
      try {
        eventStore.remove(eventId);
        // Assignment will be auto-removed
      } catch (error) {
        console.error("Error deleting schedule:", error);
        alert("Failed to delete schedule. Please try again.");
      }
    }
  }, [project, confirm]);

  // CRUD operations - Planning
  const handleCreatePlanning = useCallback((defaultFormValues?: Partial<PlanningFormData>) => {
    setPlanningEditModal({ isOpen: true, mode: "create", defaultFormValues });
  }, []);

  const handleEditPlanning = useCallback((planningId: string) => {
    setPlanningEditModal({ isOpen: true, mode: "edit", planningId });
  }, []);

  const handleDeletePlanning = useCallback(async (planningId: string) => {
    if (!project) return;

    const planningStore = project.taskPlanningDatesStore;
    const planning = planningStore?.getById(planningId);

    if (!planning) {
      alert("Planning date not found");
      return;
    }

    const confirmDelete = await confirm({
      title: "Confirm Delete",
      description: `Are you sure you want to delete this planning date: "${planning.name || "Unnamed planning"}"?`,
      customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-4">
          <Button variant="ghost" onClick={cancel}>
            Cancel
          </Button>
          <Button className="dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors" onClick={confirm}>
            Delete
          </Button>
        </div>
      )
    });

    if (confirmDelete) {
      try {
        planning.remove();
        // Linked events will be auto-removed via SwivuPlanEventStore.onPlanningDateStoreChange
      } catch (error) {
        console.error("Error deleting planning date:", error);
        alert("Failed to delete planning date. Please try again.");
      }
    }
  }, [project, confirm]);

  // Register widget param actions for deep linking (URL params)
  useEffect(() => {
    callParamAction(
      WidgetParamKeys.PLANNING.CREATE,
      (value) => {
        handleCreatePlanning({
          startDate: value?.startDate,
          endDate: value?.endDate,
        });
        return true;
      }
    );

    callParamAction(
      WidgetParamKeys.PLANNING.EDIT,
      (id) => {
        handleEditPlanning(id?.toString());
        return true;
      }
    );

    callParamAction(
      WidgetParamKeys.REMINDER.CREATE,
      () => {
        handleCreateReminder();
        return true;
      }
    );

    callParamAction(
      WidgetParamKeys.REMINDER.EDIT,
      (id) => {
        const reminder = project?.reminderStore?.getById(id);
        if (!reminder) return false;
        setSelectedMode("reminders");
        openReminderModal(reminder);
        return true;
      }
    );
  }, [
    callParamAction,
    handleCreatePlanning,
    handleEditPlanning,
    handleCreateReminder,
    openReminderModal,
    project,
  ]);

  const contextValue = useMemo<TimelineContextValue>(() => ({
    record,
    entityType,
    project,
    timelineData,
    isLoading,
    isInitialLoad,
    selectedMode,
    setSelectedMode,
    reminderModal,
    statusModal,
    planningModal,
    resourceModal,
    historyModal,
    openReminderModal,
    closeReminderModal,
    openStatusModal,
    closeStatusModal,
    openPlanningModal,
    closePlanningModal,
    openResourceModal,
    closeResourceModal,
    openHistoryModal,
    closeHistoryModal,
    reminderEditModal,
    setReminderEditModal,
    scheduleEditModal,
    setScheduleEditModal,
    planningEditModal,
    setPlanningEditModal,
    handleCreateReminder,
    handleEditReminder,
    handleDeleteReminder,
    handleCreateSchedule,
    handleEditSchedule,
    handleDeleteSchedule,
    handleCreatePlanning,
    handleEditPlanning,
    handleDeletePlanning,
    handleStatusClick,
    handlePlanningEventClick,
    handleScheduleResourceClick,
    handleReminderClick,
    handleHistoryClick,
    config,
    onClose,
    headerTitle
  }), [
    record,
    entityType,
    project,
    timelineData,
    isLoading,
    isInitialLoad,
    selectedMode,
    setSelectedMode,
    reminderModal,
    statusModal,
    planningModal,
    resourceModal,
    historyModal,
    openReminderModal,
    closeReminderModal,
    openStatusModal,
    closeStatusModal,
    openPlanningModal,
    closePlanningModal,
    openResourceModal,
    closeResourceModal,
    openHistoryModal,
    closeHistoryModal,
    reminderEditModal,
    scheduleEditModal,
    planningEditModal,
    handleCreateReminder,
    handleEditReminder,
    handleDeleteReminder,
    handleCreateSchedule,
    handleEditSchedule,
    handleDeleteSchedule,
    handleCreatePlanning,
    handleEditPlanning,
    handleDeletePlanning,
    handleStatusClick,
    handlePlanningEventClick,
    handleScheduleResourceClick,
    handleReminderClick,
    handleHistoryClick,
    config,
    onClose,
    headerTitle
  ]);

  return (
    <TimelineContext.Provider value={contextValue}>
      {children}
    </TimelineContext.Provider>
  );
};

export const useTimelineContext = (): TimelineContextValue => {
  const context = useContext(TimelineContext);
  if (!context) {
    throw new Error('useTimelineContext must be used within TimelineProvider');
  }
  return context;
};
